import { useAuth } from "@clerk/tanstack-react-start";
import { useCallback, useMemo, useRef } from "react";

/**
 * ConvexProviderWithClerk’s adapter calls getToken with useCallback([orgId, orgRole]).
 * If Clerk’s useAuth returns new orgId/orgRole references every render, fetchAccessToken
 * gets a new identity → ConvexProviderWithAuth’s effect re-runs → setAuth runs again →
 * each setAuth triggers 1–2 Clerk /tokens calls. That can cause 15+ token requests on load.
 *
 * This hook provides a stable fetchAccessToken (ref-backed getToken, useCallback with [])
 * so the Convex effect only runs when isLoaded/isSignedIn actually change. getToken is
 * read from a ref so we always call the latest Clerk getToken without putting it in deps.
 */
export function useConvexClerkAuth() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      try {
        return (
          (await getTokenRef.current({
            template: "convex",
            skipCache: forceRefreshToken,
          })) ?? null
        );
      } catch {
        return null;
      }
    },
    []
  );

  return useMemo(
    () => ({
      isLoading: !isLoaded,
      isAuthenticated: isSignedIn ?? false,
      fetchAccessToken,
    }),
    [isLoaded, isSignedIn, fetchAccessToken]
  );
}
